import { push } from "connected-react-router";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { ORG_ADMIN, ORG_MEMBER, ORG_OWNER } from "@xapp/stentor-api-client/dist/models/Roles";

import { isAdmin } from "auth";
import { getRecentlyVisitedApps } from "components/RecentlyVisitedApps";
import { getRecentlyVisitedOrgs } from "components/RecentlyVisitedOrgs";
import { BETA_FLEET_MANAGEMENT, BETA_MAP_VIEW } from "constants/beta";
import {
    useGetOrgMembersWithAppIdsQuery,
    useGetOrgQuery,
    useGetOrgsQuery,
    useGetLandingAppsQuery,
    useSearchAdminAppsQuery,
} from "generated/graphql";
import { State } from "redux/state";
import { isNotNullOrUndefined } from "utils/isNotNullOrUndefined";
import { openExternalURL } from "utils/Utils";

import { NAV_DRAWER_BUSINESS_LIMIT, NavDrawer, NavDrawerBusiness, NavDrawerOrgOption } from "../components/NavDrawer";
import { getCurrentOrganizationId, getOrgRoles, NavDrawerOrganization, resolveNavDrawerRole } from "../utils";

/**
 * How many apps to fetch for business cards. Business users are scoped to a handful; this is the
 * same ceiling the org's app-name lookups use.
 */
const NAV_DRAWER_BUSINESS_CARD_LIMIT = 100;

export interface NavDrawerOwnProps {
    readonly isActive: boolean;
    onToggleClick(): void;
    /** Close the drawer. Falls back to onToggleClick for callers that only toggle. */
    onClose?(): void;
    /** Docked beside the page (wide screens) rather than overlaying it. */
    readonly docked?: boolean;
    /** Docked and collapsed to icons. */
    readonly mini?: boolean;
    onCollapse?(): void;
    onExpand?(): void;
}

function hasBeta(beta: unknown, flag: string): boolean {
    return !!beta && typeof beta === "object" && !!(beta as Record<string, unknown>)[flag];
}

export const NavDrawerContainer: FC<NavDrawerOwnProps> = (props) => {
    const { isActive, onToggleClick, onClose, docked, mini, onCollapse, onExpand } = props;
    const close = onClose || onToggleClick;

    const profile = useSelector((state: State) => state.auth.profile);
    const asUser = useSelector((state: State) => state.auth.asUser);

    const dispatch = useDispatch();
    const { pathname } = useLocation();

    // What they typed in the org picker, and the same value once they pause typing.
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);

    // The org picker and every link navigate; close the drawer whenever the route changes.
    const lastPathname = useRef(pathname);
    useEffect(() => {
        if (pathname !== lastPathname.current) {
            lastPathname.current = pathname;
            // Only the overlay gets out of the way; a docked drawer stays beside the page.
            if (isActive && !docked) {
                close();
            }
        }
    }, [pathname, isActive, close, docked]);

    const organizationId = getCurrentOrganizationId(pathname, profile);
    const orgRoles = getOrgRoles(profile, organizationId);

    // Only an org member can be limited to specific businesses by the appIds on their policy.
    const needsPolicy =
        !isAdmin(profile) &&
        !!organizationId &&
        orgRoles.includes(ORG_MEMBER) &&
        !orgRoles.includes(ORG_OWNER) &&
        !orgRoles.includes(ORG_ADMIN);

    // These run whether or not the drawer is open. Pausing them while closed and resuming on reopen,
    // with the results already cached, sent urql into a synchronous re-render loop that froze the tab.
    const [getOrgs] = useGetOrgsQuery({
        pause: false,
        requestPolicy: "cache-and-network",
        variables: {
            from: 0,
            size: 12,
            withIdOrName: debouncedSearch || undefined,
        },
    });

    const [getOrg] = useGetOrgQuery({
        pause: !organizationId,
        requestPolicy: "cache-first",
        variables: { orgId: organizationId },
    });

    const [getMembers] = useGetOrgMembersWithAppIdsQuery({
        pause: !needsPolicy,
        requestPolicy: "cache-first",
        variables: { organizationId },
    });

    const restrictedAppIds = useMemo<string[] | null>(() => {
        const self = (getMembers.data?.org?.members?.members || []).find(
            (member) => member?.userId === profile?.userId
        );
        const appIds = self?.policy?.appIds;
        return Array.isArray(appIds) ? appIds.filter(isNotNullOrUndefined) : null;
    }, [getMembers.data, profile]);

    const role = resolveNavDrawerRole({ profile, organizationId, restrictedAppIds });

    // The BUSINESSES section only lists the first few, but a business user gets a card for every
    // business they can reach, and a restricted member's may be anywhere in the org's list.
    const [getApps] = useSearchAdminAppsQuery({
        pause: !organizationId,
        requestPolicy: "cache-and-network",
        variables: {
            organizationId,
            size: role === "business" ? NAV_DRAWER_BUSINESS_CARD_LIMIT : NAV_DRAWER_BUSINESS_LIMIT,
        },
    });

    const searchedOrgs = useMemo<NavDrawerOrgOption[]>(
        () =>
            (getOrgs.data?.orgs?.orgs || [])
                .filter(isNotNullOrUndefined)
                .map((org) => ({ organizationId: org.organizationId, name: org.name, logoUrl: org.logoUrl || undefined })),
        [getOrgs.data]
    );

    // Remember whether they have several orgs from an unfiltered load, so a narrow search
    // does not make the picker disappear while they type.
    const [hasMultipleOrganizations, setHasMultipleOrganizations] = useState<boolean>(false);
    useEffect(() => {
        const total = getOrgs.data?.orgs?.total;
        // Only ever upgrade: a response that arrives mid-search can be narrower than their real total.
        if (!debouncedSearch && typeof total === "number" && total > 1) {
            setHasMultipleOrganizations(true);
        }
    }, [getOrgs.data, debouncedSearch]);

    // Before they search, an admin sees the orgs they visited recently rather than an arbitrary first page.
    const organizations = useMemo<NavDrawerOrgOption[]>(() => {
        if (debouncedSearch || !isAdmin(profile)) {
            return searchedOrgs;
        }
        const recent = isActive ? getRecentlyVisitedOrgs() : [];
        return recent.length > 0
            ? recent.map((org) => ({ organizationId: org.organizationId, name: org.name, logoUrl: org.logoUrl }))
            : searchedOrgs;
        // pathname: re-read the recents on every navigation, like recentOrganizations below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, profile, searchedOrgs, isActive, pathname]);

    const org = getOrg.data?.org;
    const listedOrgName = organizations.find((o) => o.organizationId === organizationId)?.name;
    const organizationName = org?.name || listedOrgName;

    const organization = useMemo<NavDrawerOrganization | undefined>(() => {
        if (!organizationId || (!org && !listedOrgName)) {
            return undefined;
        }

        return {
            organizationId,
            name: organizationName || organizationId,
            isPartner: org?.type === "PARTNER",
            hasFleetManagement: hasBeta(org?.beta, BETA_FLEET_MANAGEMENT),
            hasMapView: hasBeta(org?.beta, BETA_MAP_VIEW),
        };
    }, [organizationId, org, listedOrgName, organizationName]);

    const businesses = useMemo<NavDrawerBusiness[]>(() => {
        const apps = (getApps.data?.apps?.apps || []).filter(isNotNullOrUndefined).map((app) => ({
            appId: app.appId,
            organizationId: app.organizationId || organizationId,
            name: app.name,
            status: app.status?.type,
            logoUrl: app.smallIcon || app.largeIcon || undefined,
        }));

        // Belt and braces: a restricted member only ever sees the businesses on their policy.
        return restrictedAppIds && restrictedAppIds.length > 0
            ? apps.filter((app) => restrictedAppIds.includes(app.appId))
            : apps;
    }, [getApps.data, organizationId, restrictedAppIds]);

    // How many businesses they can reach at all, for "All businesses" when no org is selected.
    const [getAllApps] = useGetLandingAppsQuery({
        pause: !!organizationId,
        requestPolicy: "cache-and-network",
    });
    const businessesTotal = restrictedAppIds && restrictedAppIds.length > 0
        ? restrictedAppIds.length
        : getApps.data?.apps?.total ?? undefined;

    // Where they were recently, for when no organization is selected. Read on open so it is current.
    const recentOrganizations = useMemo<NavDrawerOrgOption[]>(
        () =>
            isActive
                ? getRecentlyVisitedOrgs().map((o) => ({ organizationId: o.organizationId, name: o.name, logoUrl: o.logoUrl }))
                : [],
        // Re-read on every navigation: a docked drawer stays open, so "on open" would be once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isActive, pathname]
    );
    const recentBusinesses = useMemo(
        () =>
            isActive
                ? getRecentlyVisitedApps().map((a) => ({
                      appId: a.appId,
                      organizationId: a.orgId,
                      name: a.name,
                      organizationName: a.orgName,
                      logoUrl: a.smallIcon,
                  }))
                : [],
        // Re-read on every navigation: a docked drawer stays open, so "on open" would be once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isActive, pathname]
    );

    const handleNavClick = useCallback((path: string) => dispatch(push(path)), [dispatch]);
    const handleExternalClick = useCallback((url: string) => openExternalURL(url), []);
    // The route change closes the drawer.
    const handleSelectOrganization = useCallback((id: string) => dispatch(push(`/${id}`)), [dispatch]);

    return (
        <NavDrawer
            open={isActive}
            role={role}
            profile={profile}
            asUser={asUser}
            organization={organization}
            organizationName={organizationName}
            organizations={organizations}
            businesses={businesses}
            onClose={close}
            onNavClick={handleNavClick}
            onExternalClick={handleExternalClick}
            onSelectOrganization={handleSelectOrganization}
            onOrgSearchChange={setSearch}
            // Busy from the first keystroke: while the debounce waits and while the search runs.
            orgSearchLoading={search.trim() !== debouncedSearch || (!!debouncedSearch && !!getOrgs.fetching)}
            hasMultipleOrganizations={hasMultipleOrganizations}
            recentOrganizations={recentOrganizations}
            recentBusinesses={recentBusinesses}
            businessesTotal={businessesTotal}
            allBusinessesTotal={getAllApps.data?.apps?.total ?? undefined}
            docked={docked}
            mini={mini}
            onCollapse={onCollapse}
            onExpand={onExpand}
            currentPath={pathname}
        />
    );
};

export default NavDrawerContainer;
